import React, { useCallback, useEffect, useId, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeModal, selectActiveModalData } from 'features/modal/modalSlice';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from 'components/Dialog';
import { UserId } from 'features/users/types';
import { contentLibraryFileShareRequested } from 'features/content-library/actions';
import { selectFile } from 'features/content-library/contentLibrarySlice';
import { useContentLibrary } from 'features/content-library/useContentLibrary';
import ShareFileUsersSelector from 'features/content-library/components/share-users-selector/ShareFileUsersSelector';
import { selectAvailableUserIdsWithoutLocal } from 'features/users/usersSlice';

const ShareContentLibraryFileModal = () => {
  const { t } = useTranslation('modals');
  const dispatch = useAppDispatch();

  const modalId = useId();

  const modalData = useAppSelector(selectActiveModalData);
  const fileId = modalData.fileId as string;

  const file = useAppSelector((state) => selectFile(state, fileId));
  const availableUserIds = useAppSelector(selectAvailableUserIdsWithoutLocal);

  const [selectedIds, setSelectedIds] = useState<UserId[]>(availableUserIds);
  const [shareWithAll, setShareWithAll] = useState(true);

  const { isFeatureAvailable, canManageLibrary } = useContentLibrary();

  // Sync selected users based on shareWithAll state
  useEffect(() => {
    if (shareWithAll) {
      setSelectedIds(availableUserIds);
    } else {
      // Filter out users who left
      setSelectedIds((prevSelectedIds) =>
        prevSelectedIds.filter((id) => availableUserIds.includes(id))
      );
    }
  }, [availableUserIds, shareWithAll]);

  const modalShouldBeClosed = !isFeatureAvailable || !canManageLibrary || !file;

  useEffect(() => {
    if (modalShouldBeClosed) {
      dispatch(closeModal('shareContentLibraryFile'));
    }
  }, [modalShouldBeClosed, dispatch]);

  const handleCancel = () => {
    dispatch(closeModal('shareContentLibraryFile'));
  };

  const handleOk = () => {
    if (!file) {
      return;
    }

    dispatch(
      contentLibraryFileShareRequested({
        id: fileId,
        personal: file.personal,
        targetUsers: shareWithAll ? undefined : selectedIds,
      })
    );
    dispatch(closeModal('shareContentLibraryFile'));
  };

  const handleUserToggle = useCallback((id: UserId) => {
    setSelectedIds((prevSelectedIds) => {
      const currentIndex = prevSelectedIds.indexOf(id);
      return currentIndex === -1
        ? [...prevSelectedIds, id]
        : prevSelectedIds.filter((selectedId) => selectedId !== id);
    });
  }, []);

  const handleSelectAllChange = useCallback(
    (checked: boolean) => {
      setShareWithAll(checked);
      if (checked) {
        setSelectedIds(availableUserIds);
      } else {
        setSelectedIds([]);
      }
    },
    [availableUserIds]
  );

  return (
    <Dialog
      open={!modalShouldBeClosed}
      maxWidth="xs"
      aria-labelledby={`${modalId}-title`}
      aria-describedby={`${modalId}-description`}
    >
      <DialogTitle id={`${modalId}-title`}>{t('content_library.share_file.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText id={`${modalId}-description`}>
          <Trans
            i18nKey="modals:content_library.share_file.description"
            t={t}
            values={{
              fileName: file?.name,
            }}
            components={{
              bold: <strong />,
            }}
          />
        </DialogContentText>
        <Box
          sx={{
            mt: 6,
          }}
        >
          <ShareFileUsersSelector
            selectedIds={selectedIds}
            toggleUser={handleUserToggle}
            shareWithAll={shareWithAll}
            onSelectAllChange={handleSelectAllChange}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="grayscale">
          {t('content_library.share_file.btn_cancel_label')}
        </Button>
        <Button onClick={handleOk} disabled={!shareWithAll && !selectedIds.length}>
          {t('content_library.share_file.btn_share_label')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareContentLibraryFileModal;
